import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

import { environment } from '../../environments/environment';

/**
 * Runtime consent config for the standalone exhibitor profile widget.
 *
 * Unlike the main SPA (which runs RuntimeConfigService as an APP_INITIALIZER),
 * the widget is built with a baked-in environment.prod-*.ts, so the Legal-tab
 * edits (consent text + which checkboxes show) never reach it. This initializer
 * fetches GET {serverUrl}/public-legal once at boot and patches `environment`
 * BEFORE the custom element is defined, so the component's field initializers
 * and form validators see the live values.
 *
 * Entirely best-effort: any error/timeout resolves without changes, leaving the
 * build-time defaults in place so the widget always renders.
 */
interface PublicLegalResponse {
  status: number;
  legal?: { [role: string]: { [type: string]: { [lang: string]: string } } };
  languages?: { [role: string]: string };
  show?: { [flag: string]: boolean };
  links?: { [key: string]: string };
}

export function loadWidgetConsentConfig(http: HttpClient): () => Promise<void> {
  return () => {
    const env: any = environment;
    if (!env.serverUrl) {
      return Promise.resolve();
    }

    return firstValueFrom(http.get<PublicLegalResponse>(`${env.serverUrl}/public-legal`))
      .then((res) => {
        if (!res) {
          return;
        }
        if (res.legal) {
          env.legal = res.legal;
        }
        if (res.languages) {
          env.legalLanguages = res.languages;
        }
        // Visibility flags (visitor_show_* / exhibitor_show_*): apply each
        // returned key onto the matching environment field.
        if (res.show) {
          for (const key of Object.keys(res.show)) {
            env[key] = !!res.show[key];
          }
        }
        // Consent PDF links.
        if (res.links) {
          for (const key of Object.keys(res.links)) {
            if (typeof res.links[key] === 'string') {
              env[key] = res.links[key];
            }
          }
        }
      })
      .catch(() => {
        // keep build-time defaults
      });
  };
}
